import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import api, { formatError } from "@/lib/api";
import { X, Send, Loader2, MessageCircle } from "lucide-react";

/**
 * CommentsSheet — Batch 4 (iter79)
 * Bottom-Sheet für Post-Kommentare. Nutzt GET/POST /api/posts/{id}/comments.
 */
export default function CommentsSheet({ postId, onClose }) {
  const { t, i18n } = useTranslation();
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [text, setText] = useState("");
  const [posting, setPosting] = useState(false);
  const [error, setError] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/posts/${postId}/comments`);
      setComments(data.comments || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [postId]);

  const submit = async () => {
    if (!text.trim()) return;
    setPosting(true); setError("");
    try {
      await api.post(`/posts/${postId}/comments`, { body: text.trim() });
      setText("");
      await load();
    } catch (e) { setError(formatError(e) || t("common.error_generic")); }
    finally { setPosting(false); }
  };

  return (
    <div data-testid="comments-sheet" className="fixed inset-0 z-[70] bg-black/50 flex items-end sm:items-center justify-center" onClick={onClose}>
      <div className="bg-white w-full sm:max-w-md sm:rounded-lg max-h-[75vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-4 py-3 border-b border-[#E5E5E5]">
          <div className="flex items-center gap-2">
            <MessageCircle size={16} className="text-[#666]" />
            <p className="text-sm font-semibold">{t("feed.comments_title")}</p>
            <span className="text-xs text-[#999]">({comments.length})</span>
          </div>
          <button data-testid="comments-close" onClick={onClose}><X size={18} className="text-[#666]" /></button>
        </div>
        <div className="flex-1 overflow-y-auto px-4 py-2 space-y-3">
          {loading ? (
            <div className="flex justify-center py-8"><Loader2 size={20} className="animate-spin text-[#999]" /></div>
          ) : comments.length === 0 ? (
            <p className="text-center text-xs text-[#999] py-8">{t("feed.comments_empty")}</p>
          ) : (
            comments.map((c) => (
              <div key={c.comment_id} data-testid={`comment-${c.comment_id}`} className="flex gap-2">
                {c.author?.avatar_url ? (
                  <img src={c.author.avatar_url} alt="" className="w-8 h-8 rounded-full object-cover shrink-0" />
                ) : (
                  <div className="w-8 h-8 rounded-full bg-[#E5E5E5] shrink-0" />
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-xs"><span className="font-semibold">{c.author?.display_name || "?"}</span>&nbsp;<span className="text-[#666]">{c.text}</span></p>
                  <p className="text-[10px] text-[#999] mt-0.5">{new Date(c.created_at).toLocaleString(i18n.language, { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })}</p>
                </div>
              </div>
            ))
          )}
        </div>
        <div className="px-4 py-3 border-t border-[#E5E5E5] bg-white">
          {error && <p className="text-xs text-red-500 mb-1">{error}</p>}
          <div className="flex gap-2">
            <input
              data-testid="comments-input"
              value={text}
              onChange={(e) => setText(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && !posting && submit()}
              placeholder={t("feed.write_comment")}
              className="flex-1 h-10 px-3 border border-[#E5E5E5] text-sm focus:outline-none focus:ring-1 focus:ring-[#0A0A0A]"
              maxLength={500}
            />
            <button
              data-testid="comments-submit"
              onClick={submit}
              disabled={posting || !text.trim()}
              className="rounded-full h-10 px-4 bg-[#0A0A0A] text-white text-sm disabled:opacity-30">
              {posting ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
